/**
 * Renderer-agnostic highlight engine. Works on the rendered DOM of a page
 * container (plain text or pdf.js text layer): builds a concatenated text
 * index over all text nodes, finds quote/search ranges in it, and paints them
 * with the CSS Custom Highlight API, no DOM mutation, so it never fights
 * React or pdf.js over the text layer.
 *
 * Matching is whitespace-flexible in both directions (LLM quotes may collapse
 * line breaks, the pdf.js text layer may drop spaces at line wraps) and folds
 * typographic characters through the SAME table the backend verifier uses
 * (orion/anchors.py `_FOLDS`), so a backend-verified quote is findable here by
 * construction, and the two can never disagree on a character class.
 *
 * Ported verbatim from the regulatory-cms studio (document-viewer/highlights.ts
 * + core/anchors.ts), the swap-ready contract the studio was built to hand off.
 */

export interface TextIndex {
  text: string
  nodes: { node: Text; start: number }[]
}

export interface Match {
  start: number
  end: number
}

export function buildTextIndex(container: HTMLElement): TextIndex {
  const nodes: TextIndex["nodes"] = []
  let text = ""
  // pdf.js (TextLayer#appendText, pdfjs-dist@4.4.168 build/pdf.mjs ~L10577-10580)
  // renders a soft line wrap as a sibling `<br role="presentation">` appended
  // AFTER the line's own `<span>`, never a "\n" character. A text-node-only
  // walk is blind to that `<br>`, so a wrapped hyphen ("Kandi-<br>datenliste")
  // reads as one unbroken "Kandi-datenliste" with no gap to fold on at all
  // (TUR-1211 round 2). Walk elements too, purely to notice a `<br>`, and emit
  // a synthetic "\n" for it, it contributes zero raw characters of its own
  // (no `nodes` entry), so a Range endpoint landing on it is resolved by
  // `locate`'s gap fallback to the position right after it.
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
    {
      acceptNode(node) {
        if (node.nodeType === Node.TEXT_NODE) return NodeFilter.FILTER_ACCEPT
        if (
          node.nodeType === Node.ELEMENT_NODE &&
          (node as Element).tagName === "BR"
        ) {
          return NodeFilter.FILTER_ACCEPT
        }
        // Any other element (the text layer's own <span> wrappers, marked
        // content spans, ...): not itself a match, but its children still
        // need visiting, so SKIP rather than REJECT.
        return NodeFilter.FILTER_SKIP
      },
    },
  )
  let node: Node | null
  while ((node = walker.nextNode())) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      text += "\n"
      continue
    }
    nodes.push({ node: node as Text, start: text.length })
    text += node.nodeValue ?? ""
  }
  return { text, nodes }
}

// ── Character folding (shared table with the backend verifier) ──────────────

/**
 * Character folding shared by EVERY anchor matcher, the backend verification
 * (orion/anchors.py) and this viewer's text-layer matching fold through the one
 * table, so "verified" and "findable in the viewer" can never disagree on a
 * character class. The folds are the typographic variants real PDFs print where
 * the model quotes ASCII (or vice versa): curly quotes, dash family, ellipsis,
 * unexpanded ligatures, invisible joiners, and the fullwidth-ASCII block CJK
 * declarations mix in. Whitespace is deliberately NOT folded here, each
 * consumer keeps its own whitespace discipline (collapse-to-one-space in the
 * identity keys; \s*-flexible regex below).
 */
const FOLDS: Record<string, string> = {
  // apostrophes / single quotes → '
  "‘": "'",
  "’": "'",
  "‚": "'",
  "‛": "'",
  "′": "'",
  "´": "'",
  "`": "'",
  // double quotes → "
  "“": '"',
  "”": '"',
  "„": '"',
  "‟": '"',
  "″": '"',
  "«": '"',
  "»": '"',
  // hyphen / dash family, minus sign → -
  "‐": "-",
  "‑": "-",
  "‒": "-",
  "–": "-",
  "—": "-",
  "―": "-",
  "−": "-",
  "…": "...",
  // ligatures pdf.js occasionally leaves unexpanded
  ﬀ: "ff",
  ﬁ: "fi",
  ﬂ: "fl",
  ﬃ: "ffi",
  ﬄ: "ffl",
  ﬅ: "ft",
  ﬆ: "st",
  // invisible: soft hyphen, zero-width space / non-joiner / joiner
  "­": "",
  "​": "",
  "‌": "",
  "‍": "",
  // table cell separators (pipe family) → ' ' (backend parity, TUR-1211): a
  // model quoting a table row types " | " between cells while the PDF text
  // layer has the cells on separate lines; folding the bar to whitespace
  // lets the \s*-flexible pattern below treat the two spellings the same.
  "|": " ",
  "｜": " ",
  "¦": " ",
}

/** Fold one character to its canonical form ("" = drop, may expand to several). */
export function foldChar(ch: string): string {
  const mapped = FOLDS[ch]
  if (mapped !== undefined) return mapped
  const code = ch.charCodeAt(0)
  // Fullwidth ASCII (！→ !, Ａ → A, ： → :), bilingual declarations mix the two.
  if (code >= 0xff01 && code <= 0xff5e)
    return String.fromCharCode(code - 0xfee0)
  return ch
}

/** Fold a whole string (quote side, no offset map needed). */
function foldString(s: string): string {
  let out = ""
  for (const ch of s) out += foldChar(ch)
  return out
}

// ── Wrap-hyphen + tight-punctuation tolerance (backend parity, TUR-1211) ─────
//
// orion/anchors.py's default verification reading tolerates two more PDF
// extraction artifacts beyond the FOLDS table: a hyphen glued to a line wrap
// (kept; only the line break is dropped, a wrapped compound name like
// "REACH-Verordnung" must not lose its real hyphen), and whitespace padding
// right after `([{` or right before `)]},.;:%` (a model never types the
// padding a PDF's table/parenthetical cells leave in, e.g. "( w/w )"). Both
// folds are mirrored here so "verified" (computed on the page's raw text) and
// "findable" (matched against the pdf.js text layer) can never disagree.

// A soft line-wrap tail: an optional space/tab, an optional \r, the \n, then
// any indent on the next line, the artifact PDF extraction leaves at a
// wrapped hyphen. Only this tail is dropped; the hyphen itself is real
// content and is kept (mirrors orion/anchors.py `_WRAP_TAIL_RE` +
// `_normalize_core(drop_wrap_hyphen=False)`, the reading the backend verifier
// tries FIRST).
const WRAP_TAIL_RE = /^[ \t]?\r?\n[ \t]*/

function isLetter(ch: string): boolean {
  return /\p{L}/u.test(ch)
}

// The char AFTER a wrapped hyphen may be a digit as well as a letter: a table
// cell wraps an identifier such as a SCIP number "8f2a61c0-4d5e-4b7a-" /
// "9c31-77e0d2a4f915" at one of its own hyphens, and the model quotes the id
// unbroken. Mirrors orion/anchors.py `_normalize_core` (`isalnum()` on the
// right-hand guard only; the char BEFORE the hyphen must still be a letter, so
// a same-line digit hyphen such as a CAS number is never touched).
function isLetterOrDigit(ch: string): boolean {
  return /[\p{L}\p{Nd}]/u.test(ch)
}

// A space is dropped right after one of these openers...
const TIGHT_PUNCT_OPEN = new Set(["(", "[", "{"])
// ...or right before one of these, closers, plus `.,;:%` where a LEADING
// space is always an extraction artifact, never intentional spacing (mirrors
// orion/anchors.py `_TIGHT_PUNCT_OPEN`/`_TIGHT_PUNCT_CLOSE`). The space AFTER
// a comma/period is ordinary prose and is left untouched.
const TIGHT_PUNCT_CLOSE = new Set([")", "]", "}", ",", ".", ";", ":", "%"])

/**
 * The identity of a passage: folded through the table above, whitespace
 * collapsed to one space, trimmed, lowercased. Every de-duplication that has to
 * agree with the backend on "the same passage" keys on this, so the
 * curly-apostrophe copy of a clause is not a second clause.
 */
export function normalizeAnchorText(s: string | null | undefined): string {
  return foldString(s ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
}

/**
 * Fold the page text with an offset map back to raw indices (`map[i]` = raw
 * index of the char that produced folded char `i`), so matches found in folded
 * space convert back to DOM ranges over the raw text nodes. Memoized in a
 * four-entry LRU: `findQuote` folds the QUOTE twice and the PAGE up to
 * twice per call, and the callers loop per page over many quotes, with a
 * single slot the quote folds evicted the page fold on every call, and the
 * page was refolded per quote (review, wave 2). Four entries hold
 * exactly one call's working set, so the next quote's page folds are hits.
 */
type Fold = {
  raw: string
  dropWrapHyphen: boolean
  folded: string
  map: number[]
}
const FOLD_CACHE_SIZE = 4
const foldCache: Fold[] = []
function foldedText(
  raw: string,
  dropWrapHyphen = false,
): { folded: string; map: number[] } {
  const hit = foldCache.findIndex(
    (f) => f.raw === raw && f.dropWrapHyphen === dropWrapHyphen,
  )
  if (hit >= 0) {
    const [entry] = foldCache.splice(hit, 1)
    foldCache.push(entry)
    return entry
  }
  {
    const chars: string[] = []
    const map: number[] = []
    let i = 0
    const n = raw.length
    while (i < n) {
      const rawCh = raw[i]
      // A wrap hyphen: dropping only the tail (no map entries for its raw
      // indices) means the two flanking letters end up back-to-back in the
      // folded text with no gap to bridge, no map "extension" is needed for
      // the consumed chars, since they never appear in `folded` for a match
      // to reference in the first place; `unfold` below still resolves a
      // match's raw span correctly because a DOM Range only needs its two
      // endpoints, and everything between them (dropped chars included)
      // renders regardless of whether it was part of the match.
      if (
        foldChar(rawCh) === "-" &&
        chars.length > 0 &&
        isLetter(chars[chars.length - 1])
      ) {
        const tail = WRAP_TAIL_RE.exec(raw.slice(i + 1))
        const tailEnd = tail ? i + 1 + tail[0].length : -1
        if (tail && tailEnd < n && isLetterOrDigit(raw[tailEnd])) {
          if (!dropWrapHyphen) {
            chars.push("-")
            map.push(i)
          }
          i = tailEnd
          continue
        }
      }
      for (const ch of foldChar(rawCh)) {
        chars.push(ch)
        map.push(i)
      }
      i++
    }
    const entry: Fold = { raw, dropWrapHyphen, folded: chars.join(""), map }
    foldCache.push(entry)
    if (foldCache.length > FOLD_CACHE_SIZE) foldCache.shift()
    return entry
  }
}

/** Convert a match in folded space back to raw offsets. */
function unfold(map: number[], start: number, length: number): Match {
  return { start: map[start], end: map[start + length - 1] + 1 }
}

/**
 * Exact match first; falls back to fold- and whitespace-flexible matching.
 *
 * `occurrence` (1-based) picks WHICH match, for a span the page repeats verbatim.
 * Five article paragraphs each ending "REACH: Informationspflicht nach Artikel 33
 * erfüllt" are five different proofs, and painting all of them on the first put
 * every article's evidence inside the first article's block, the wrong article's
 * paragraph, while the right article stayed selected on the card.
 *
 * The backend derives it (`citations.occurrence_in_page`) and counts in the page's
 * NORMALIZED text while this counts in the PDF text layer, which is a different
 * string. That is exactly why the coordinate is an occurrence INDEX and not a
 * character offset: the nth match of the same needle is the one thing both agree on.
 *
 * Absent, or out of range, ⇒ the first match, the historical behaviour.
 */
export function findQuote(
  text: string,
  quote: string,
  occurrence?: number,
): Match | null {
  const nth = occurrence && occurrence > 1 ? occurrence : 1
  if (nth === 1) {
    const exact = text.indexOf(quote)
    if (exact >= 0) return { start: exact, end: exact + quote.length }
  } else {
    // Walk the exact matches first, for the same reason the single-match path
    // prefers them: an exact hit needs no fold or whitespace tolerance at all.
    let from = -1
    for (let i = 0; i < nth; i++) {
      const next = text.indexOf(quote, from + 1)
      if (next < 0) break
      from = next
      if (i === nth - 1) return { start: next, end: next + quote.length }
    }
  }
  // The QUOTE is read both ways too, exactly like the backend's `_find_span`
  // (`normalize(quote)` then `_dehyphenate_wraps(quote)`): a model that
  // transcribed the page's wrap ("Kandi-\ndatenliste") against a text layer
  // that reads "Kandidatenliste" is verified by the backend and must be
  // findable here (review, wave 2). Computed BEFORE the page loop so
  // the single-entry fold cache keeps holding the page. Dedup keeps a quote
  // without a wrap hyphen at exactly the one pattern it always had.
  const patterns = Array.from(
    new Set(
      [false, true]
        .map((drop) => flexiblePattern(foldedText(quote, drop).folded))
        .filter((p): p is string => p !== null),
    ),
  )
  if (patterns.length === 0) return null
  // Two PAGE readings, tried in the same order the backend does (anchors.py
  // `locate_quote`/`_find_span`: 'default' then 'dehyph'): hyphen-kept first,
  // a wrapped COMPOUND word like "REACH-Verordnung" keeps its real hyphen,
  // then fully dehyphenated, a wrapped SINGLE word like "Kandi-/datenliste"
  // a model quotes joined, "Kandidatenliste". The second reading is tried
  // only when the first finds nothing at all (TUR-1211 round 2).
  for (const dropWrapHyphen of [false, true]) {
    const { folded, map } = foldedText(text, dropWrapHyphen)
    for (const pattern of patterns) {
      const re = new RegExp(pattern, "gi")
      let m: RegExpExecArray | null
      let seen = 0
      let last: Match | null = null
      while ((m = re.exec(folded))) {
        if (m[0].length === 0) {
          re.lastIndex++
          continue
        }
        last = unfold(map, m.index, m[0].length)
        if (++seen === nth) return last
      }
      // Fewer matches than asked for: the last one beats nothing (degrade,
      // never vanish, the same rule the narrowing ladder follows).
      if (last) return last
    }
  }
  return null
}

/** All (non-overlapping) matches of a search query, case-insensitive. */
export function findAllMatches(text: string, query: string): Match[] {
  const pattern = flexiblePattern(foldString(query))
  if (!pattern) return []
  const { folded, map } = foldedText(text)
  const matches: Match[] = []
  const re = new RegExp(pattern, "gi")
  let m: RegExpExecArray | null
  while ((m = re.exec(folded))) {
    if (m[0].length === 0) {
      re.lastIndex++
      continue
    }
    matches.push(unfold(map, m.index, m[0].length))
  }
  return matches
}

function flexiblePattern(s: string): string | null {
  const trimmed = s.trim()
  if (!trimmed) return null
  // \s* (not \s+): the text layer may have NO whitespace where the source
  // had a line break, and vice versa. Built char-by-char (rather than a
  // blanket replace) so a `\s*` can also be inserted around tight punctuation
  // even where the QUOTE itself has no whitespace there, a quote like
  // "(w/w)" must still match a page that pads it "( w/w )" (TUR-1211).
  let pattern = ""
  for (const ch of trimmed) {
    if (/\s/.test(ch)) {
      if (!pattern.endsWith("\\s*")) pattern += "\\s*"
      continue
    }
    if (TIGHT_PUNCT_CLOSE.has(ch) && !pattern.endsWith("\\s*")) {
      pattern += "\\s*"
    }
    pattern += /[.*+?^${}()|[\]\\]/.test(ch) ? `\\${ch}` : ch
    if (TIGHT_PUNCT_OPEN.has(ch)) {
      pattern += "\\s*"
    }
  }
  return pattern
}

/** Convert a [start, end) offset pair in the index into a DOM Range. */
export function rangeFromMatch(index: TextIndex, match: Match): Range | null {
  const start = locate(index, match.start, false)
  const end = locate(index, match.end, true)
  if (!start || !end) return null
  const range = document.createRange()
  range.setStart(start.node, start.offset)
  range.setEnd(end.node, end.offset)
  return range
}

function locate(
  index: TextIndex,
  offset: number,
  isEnd: boolean,
): { node: Text; offset: number } | null {
  for (let i = index.nodes.length - 1; i >= 0; i--) {
    const { node, start } = index.nodes[i]
    const within = isEnd ? offset > start : offset >= start
    if (within && offset - start <= (node.nodeValue?.length ?? 0)) {
      return { node, offset: offset - start }
    }
  }
  // Falls in a gap no real text node covers, the one raw character a <br>
  // wrap marker occupies (`buildTextIndex` above), which contributes zero raw
  // characters of its own and so has no `nodes` entry. Resolve to the START
  // of the next real text node in DOM order: the dropped gap character
  // renders regardless of which side of it a Range endpoint sits on, so
  // snapping to "the position after it" is always a safe, visually
  // equivalent placement.
  for (const { node, start } of index.nodes) {
    if (start >= offset) return { node, offset: 0 }
  }
  // Past every real node: the offset sits on a wrap marker that is the
  // container's LAST child (the page's final line itself wrapped), which a
  // trailing `\\s*` in `flexiblePattern` can consume. Snap to the end of the
  // last text node, the visually identical place, rather than losing the
  // whole highlight (review, wave 2).
  const last = index.nodes[index.nodes.length - 1]
  if (last) return { node: last.node, offset: last.node.nodeValue?.length ?? 0 }
  return null
}

/**
 * Paint ranges under a named highlight (see ::highlight() rules in
 * docHighlights.css). Higher priority paints on top where highlights overlap.
 * No-op in browsers without the Custom Highlight API.
 */
export function setNamedHighlight(
  name: string,
  ranges: Range[],
  priority = 0,
): void {
  const registry = supported()
  if (!registry) return
  if (ranges.length === 0) {
    registry.delete(name)
    return
  }
  const HighlightCtor = (globalThis as Record<string, unknown>)
    .Highlight as new (...r: Range[]) => { priority: number }
  const h = new HighlightCtor(...ranges)
  h.priority = priority
  registry.set(name, h)
}

export function clearNamedHighlight(name: string): void {
  supported()?.delete(name)
}

function supported(): {
  set(n: string, h: unknown): void
  delete(n: string): void
} | null {
  if (typeof CSS === "undefined") return null
  return ((CSS as unknown as Record<string, unknown>).highlights ??
    null) as ReturnType<typeof supported>
}

// ── Line-merged overlay rects (Studio V2's "text marker" look) ──────────────

/** Structurally what `DOMRect`/`getClientRects()` already give us, kept as a
 *  plain interface so this helper stays framework/DOM-free and test-only
 *  fixtures never need a real `DOMRect`. */
export interface DOMRectLike {
  left: number
  top: number
  right: number
  bottom: number
  width: number
  height: number
}

function verticalOverlap(a: DOMRectLike, b: DOMRectLike): number {
  return Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top))
}

function union(a: DOMRectLike, b: DOMRectLike): DOMRectLike {
  const left = Math.min(a.left, b.left)
  const top = Math.min(a.top, b.top)
  const right = Math.max(a.right, b.right)
  const bottom = Math.max(a.bottom, b.bottom)
  return { left, top, right, bottom, width: right - left, height: bottom - top }
}

/**
 * Merge the CSS Custom Highlight API's per-span `getClientRects()` output into
 * one continuous rect per visual LINE: the "text marker" look Studio V2 wants
 * instead of the choppy per-word rectangles the raw rect list paints.
 *
 * Rects under 2px in either dimension are dropped (pdf.js occasionally emits
 * zero-width slivers at span boundaries). The rects are measured in CSS px, so
 * below Fit (`scale` < 1) the threshold shrinks with the zoom: a one-character
 * quote is genuinely 1.2x2px at 50%, and a fixed 2px floor erased its overlay.
 * Zooming in never raises the floor above 2px. Remaining rects are sorted top-to-
 * bottom, then folded into a running union: a rect joins the current line when
 * its vertical overlap with that line's union is at least half the SMALLER of
 * the two heights. Loose enough that a slightly raised superscript still
 * merges with its line, tight enough that two stacked lines never fuse.
 */
export function mergeRectsIntoLines(
  rects: DOMRectLike[],
  scale = 1,
): DOMRectLike[] {
  const minSide = 2 * Math.min(scale, 1)
  const filtered = rects.filter(
    (r) => r.width >= minSide && r.height >= minSide,
  )
  if (filtered.length === 0) return []
  const sorted = [...filtered].sort((a, b) => a.top - b.top || a.left - b.left)
  const lines: DOMRectLike[] = []
  let current: DOMRectLike = sorted[0]
  for (let i = 1; i < sorted.length; i++) {
    const r = sorted[i]
    const smaller = Math.min(current.height, r.height)
    if (verticalOverlap(current, r) >= smaller * 0.5) {
      current = union(current, r)
    } else {
      lines.push(current)
      current = r
    }
  }
  lines.push(current)
  return lines
}

/** Scroll the start of a range into view within the nearest scroll container.
 *  `behavior: "auto"` snaps, used while sweeping/stepping, where a smooth
 *  animation would lag behind rapid stepping. */
export function scrollRangeIntoView(
  range: Range,
  behavior: ScrollBehavior = "smooth",
): void {
  const el =
    range.startContainer instanceof Element
      ? range.startContainer
      : range.startContainer.parentElement
  if (el) scrollElementIntoView(el, behavior, "center")
}

/**
 * scrollIntoView with a long-jump guard: beyond a couple of viewport heights,
 * snap instead of smooth. Chromium CANCELS smooth scrolls whenever the scroll
 * container's layout shifts, which is constant while a large PDF rasterizes,
 * so a long smooth jump silently goes nowhere; and past that distance the
 * landing is what orients the user, not the travel.
 */
export function scrollElementIntoView(
  el: Element,
  behavior: ScrollBehavior = "smooth",
  block: ScrollLogicalPosition = "center",
): void {
  const top = el.getBoundingClientRect().top
  const far = Math.abs(top - window.innerHeight / 2) > window.innerHeight * 2
  scrollWithoutMovingThePage(() =>
    el.scrollIntoView({
      behavior: behavior === "smooth" && far ? "auto" : behavior,
      block,
    }),
  )
}

/**
 * `Element.scrollIntoView` scrolls every scrollable ancestor needed to bring
 * the target into view, not only the box that actually owns it, Chromium's
 * ancestor walk reaches all the way up to `document.scrollingElement`. The
 * viewer's own scroll container (`scrollRef`'s div) is the only box a jump
 * should ever move.
 *
 * On Mac the page sits at exactly 100vh (overlay scrollbars reserve no
 * layout space) and the walk finds nothing to move there; on Windows,
 * classic scrollbars carve their own width/height out of the layout, which
 * is enough to leave the outer page a few px genuinely scrollable, so the
 * SAME jump nudges the whole page (TUR-1211, reported Windows Chrome only,
 * sandbox 2026-09-15: clicking a declaration in "Erklärungen auswerten"
 * scrolled the whole page).
 *
 * Fix: run the scroll, then snap the page's own position back if it moved.
 * `scrollTop`/`scrollLeft` assignment is a synchronous, instant request that
 * supersedes whatever the just-issued call queued for that same box, since
 * this runs in the same task, before the browser has painted a frame (even
 * for `behavior: "smooth"`, whose animation starts on a later frame), the
 * net effect is that the page never visibly moves. `document.scrollingElement`
 * is `<html>` in every real browser; it falls back to `documentElement` only
 * because jsdom (tests) never sets it.
 */
export function scrollWithoutMovingThePage(scroll: () => void): void {
  const page = document.scrollingElement ?? document.documentElement
  const top = page.scrollTop
  const left = page.scrollLeft
  scroll()
  // Assigned unconditionally: with `behavior: "smooth"` Chromium may leave
  // `scrollTop` unchanged until the animation's next frame, so a "did it
  // move" guard would skip the restore and the queued page scroll would
  // still run. An instant assignment cancels that queued smooth scroll.
  page.scrollTop = top
  page.scrollLeft = left
}
